import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { 
  Stethoscope, Mail, Lock, Phone, Building2, MapPin, 
  Check, Calendar, Users, Bell, BarChart3, Settings,
  CalendarDays, Clock, CreditCard, X, AlertCircle, HourglassIcon,
  FileText, Download, CalendarX, Trash2, Plus, Save, Link2, Unlink,
  PawPrint, Syringe, Pill, AlertTriangle, Eye, ChevronRight, FileSpreadsheet,
  Video, User, Repeat
} from 'lucide-react';
import { toast } from 'sonner';
import { useSiteSettings } from '../context/SiteSettingsContext';
import IntegrationManager from '../components/vet/IntegrationManager';
import AgendaCalendar from '../components/vet/AgendaCalendar';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';
import PromoBanner from '../components/promo/PromoBanner';
import Teleconsultation from '../components/Teleconsultation';
import PricingComparison from '../components/pricing/PricingComparison';
import { VetProfileEditor } from '../components/profile/ProfileEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Medical record type config for vet view
const RECORD_TYPES = {
  vaccination: { label: 'Vaccination', icon: Syringe, color: 'bg-blue-500', bgLight: 'bg-blue-50', textColor: 'text-blue-700' },
  traitement: { label: 'Traitement', icon: Pill, color: 'bg-green-500', bgLight: 'bg-green-50', textColor: 'text-green-700' },
  chirurgie: { label: 'Chirurgie', icon: Stethoscope, color: 'bg-red-500', bgLight: 'bg-red-50', textColor: 'text-red-700' },
  allergie: { label: 'Allergie', icon: AlertTriangle, color: 'bg-orange-500', bgLight: 'bg-orange-50', textColor: 'text-orange-700' },
  consultation: { label: 'Consultation', icon: FileText, color: 'bg-gray-500', bgLight: 'bg-gray-50', textColor: 'text-gray-700' }
};

const EspaceVeterinairePage = () => {
  const { settings } = useSiteSettings();
  const pricingPlans = settings.pricing?.plans || [];
  const [searchParams] = useSearchParams();
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [vetProfile, setVetProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [dashboardTab, setDashboardTab] = useState('appointments');
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [showPricingModal, setShowPricingModal] = useState(false);
  
  // Shared animals for vet
  const [sharedAnimals, setSharedAnimals] = useState([]);
  const [selectedSharedAnimal, setSelectedSharedAnimal] = useState(null);
  const [sharedAnimalRecords, setSharedAnimalRecords] = useState([]);
  const [showAddRecordForm, setShowAddRecordForm] = useState(false);
  const [newRecordForm, setNewRecordForm] = useState({
    record_type: 'consultation',
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  // Real data from API
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({
    total_this_month: 0,
    completed_this_month: 0,
    pending: 0,
    confirmed: 0,
    unique_patients: 0,
    completion_rate: 0
  });
  const [loadingData, setLoadingData] = useState(false);
  
  // Schedule state
  const [schedule, setSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [recurringSlots, setRecurringSlots] = useState([]);
  
  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  
  // Google Calendar state
  const [googleCalendar, setGoogleCalendar] = useState({ connected: false, email: null });
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  
  // Teleconsultation state
  const [showTeleconsultation, setShowTeleconsultation] = useState(false);
  const [teleconsultationData, setTeleconsultationData] = useState(null);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    clinicName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    password: '',
    confirmPassword: '',
    selectedPlan: 'pro'
  });

  // Fetch appointments and stats
  const fetchVetData = async () => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    setLoadingData(true);
    try {
      // Fetch appointments
      const apptResponse = await fetch(`${API_URL}/api/appointments/vet`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (apptResponse.ok) {
        const apptData = await apptResponse.json();
        setAppointments(apptData);
      }
      
      // Fetch stats
      const statsResponse = await fetch(`${API_URL}/api/appointments/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
      
      // Fetch shared animals
      fetchSharedAnimals();
    } catch (error) {
      console.error('Error fetching vet data:', error);
    }
    setLoadingData(false);
  };

  // Fetch shared animals for vet
  const fetchSharedAnimals = async () => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/animals/vet/shared`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSharedAnimals(data);
      }
    } catch (error) {
      console.error('Error fetching shared animals:', error);
    }
  };

  // Fetch medical records for a shared animal
  const fetchAnimalRecords = async (animalId) => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/animals/${animalId}/records`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSharedAnimalRecords(data);
      }
    } catch (error) {
      console.error('Error fetching animal records:', error);
    }
  };

  // Add medical record to shared animal
  const handleAddRecord = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('vetToken');
    if (!token || !selectedSharedAnimal) return;
    
    try {
      const response = await fetch(`${API_URL}/api/animals/${selectedSharedAnimal.animal_id}/records`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          ...newRecordForm,
          animal_id: selectedSharedAnimal.animal_id,
          vet_name: vetProfile?.name
        })
      });

      if (response.ok) {
        toast.success('Événement ajouté au carnet de santé');
        fetchAnimalRecords(selectedSharedAnimal.animal_id);
        setShowAddRecordForm(false);
        setNewRecordForm({
          record_type: 'consultation',
          title: '',
          description: '',
          date: new Date().toISOString().split('T')[0]
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'ajout');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Fetch schedule
  const fetchSchedule = async () => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    setLoadingSchedule(true);
    try {
      const response = await fetch(`${API_URL}/api/schedules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSchedule(data);
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
    setLoadingSchedule(false);
  };

  // Fetch recurring slots (Pro/Multi only)
  const fetchRecurringSlots = async () => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/schedules/recurring-slots`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRecurringSlots(data);
      }
    } catch (error) {
      // Silently fail for Essentiel plan users
      console.log('Recurring slots not available for this plan');
    }
  };

  // Update schedule
  const updateSchedule = async (updates) => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/schedules`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        const data = await response.json();
        setSchedule(data);
        toast.success('Horaires mis à jour');
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error('Erreur de connexion');
    }
  };

  // Add blocked date
  const addBlockedDate = async () => {
    if (!newBlockedDate) return;
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/schedules/blocked-dates?date=${newBlockedDate}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSchedule(prev => ({
          ...prev,
          blocked_dates: [...(prev?.blocked_dates || []), newBlockedDate]
        }));
        setNewBlockedDate('');
        toast.success('Date bloquée ajoutée');
      }
    } catch (error) {
      console.error('Error adding blocked date:', error);
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Remove blocked date
  const removeBlockedDate = async (date) => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/schedules/blocked-dates/${date}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSchedule(prev => ({
          ...prev,
          blocked_dates: (prev?.blocked_dates || []).filter(d => d !== date)
        }));
        toast.success('Date débloquée');
      }
    } catch (error) {
      console.error('Error removing blocked date:', error);
    }
  };

  // Fetch Google Calendar status
  const fetchGoogleCalendarStatus = async () => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/google/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGoogleCalendar(data);
      }
    } catch (error) {
      console.error('Error fetching Google status:', error);
    }
  };

  // Connect Google Calendar
  const connectGoogleCalendar = async () => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    setLoadingGoogle(true);
    try {
      const response = await fetch(`${API_URL}/api/google/auth`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authorization_url;
      } else {
        toast.error('Erreur lors de la connexion à Google');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setLoadingGoogle(false);
  };

  // Disconnect Google Calendar
  const disconnectGoogleCalendar = async () => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/google/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setGoogleCalendar({ connected: false, email: null });
        toast.success('Google Agenda déconnecté');
      }
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  // Sync all appointments to Google
  const syncAllToGoogle = async () => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    setLoadingGoogle(true);
    try {
      const response = await fetch(`${API_URL}/api/google/sync-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        toast.error('Erreur lors de la synchronisation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setLoadingGoogle(false);
  };

  // Check for Google callback params
  useEffect(() => {
    if (searchParams.get('google_success')) {
      toast.success('Google Agenda connecté avec succès !');
      fetchGoogleCalendarStatus();
    } else if (searchParams.get('google_error')) {
      toast.error('Erreur de connexion à Google Agenda');
    }
  }, [searchParams]);

  // Fetch invoices
  const fetchInvoices = async () => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    setLoadingInvoices(true);
    try {
      const response = await fetch(`${API_URL}/api/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
    setLoadingInvoices(false);
  };

  // Download invoice as PDF
  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    try {
      toast.loading('Génération du PDF en cours...');
      
      const response = await fetch(`${API_URL}/api/invoices/${invoiceId}/download?format=pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Download PDF file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `facture_${invoiceNumber || invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.dismiss();
        toast.success('Facture téléchargée');
      } else {
        toast.dismiss();
        toast.error('Erreur lors du téléchargement');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.dismiss();
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Preview invoice as HTML in browser
  const previewInvoice = async (invoiceId) => {
    const token = localStorage.getItem('vetToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/invoices/${invoiceId}/preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const html = await response.text();
        const printWindow = window.open('', '_blank');
        printWindow.document.write(html);
        printWindow.document.close();
      }
    } catch (error) {
      console.error('Error previewing invoice:', error);
      toast.error('Erreur lors de la prévisualisation');
    }
  };

  // Fetch data when logged in with approved status
  useEffect(() => {
    if (isLoggedIn && vetProfile?.status === 'approved') {
      fetchVetData();
      fetchSchedule();
      fetchInvoices();
      fetchGoogleCalendarStatus();
      fetchRecurringSlots();
    }
  }, [isLoggedIn, vetProfile?.status]);

  // Get selected plan details
  const getSelectedPlanDetails = () => {
    return pricingPlans.find(p => p.id === (vetProfile?.subscription_plan || selectedPlan)) || pricingPlans[1] || {
      name: 'Pro',
      price: 49,
      maxVets: 2,
      appointmentsLimit: 200
    };
  };

  const currentPlan = getSelectedPlanDetails();

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('vetToken');
      if (token) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` },
            
          });
          if (response.ok) {
            const data = await response.json();
            if (data.role === 'veterinarian') {
              setIsLoggedIn(true);
              // Fetch vet profile
              const vetResponse = await fetch(`${API_URL}/api/veterinarians/me`, {
                headers: { 'Authorization': `Bearer ${token}` },
                
              });
              if (vetResponse.ok) {
                const vetData = await vetResponse.json();
                setVetProfile(vetData);
              }
            }
          }
        } catch (error) {
          console.error('Auth check failed:', error);
        }
      }
    };
    checkAuth();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(loginForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.user.role !== 'veterinarian') {
          toast.error('Ce compte n\'est pas un compte vétérinaire');
          setIsLoading(false);
          return;
        }
        
        localStorage.setItem('vetToken', data.access_token);
        
        // Check user status
        const userStatus = data.user.status || 'pending';
        
        if (userStatus === 'rejected') {
          toast.error('Votre compte a été refusé par l\'administrateur.');
          setIsLoading(false);
          return;
        }
        
        // Set profile with status
        const profile = data.vet_profile || {};
        profile.status = userStatus;
        profile.user = data.user;
        
        setVetProfile(profile);
        setIsLoggedIn(true);
        
        if (userStatus === 'pending') {
          toast.success('Connexion réussie ! Votre compte est en attente de validation.');
        } else {
          toast.success('Connexion réussie !');
        }
      } else {
        toast.error(data.detail || 'Email ou mot de passe incorrect');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    }
    setIsLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Step 1: Register user
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          email: registerForm.email,
          password: registerForm.password,
          name: `${registerForm.firstName} ${registerForm.lastName}`,
          role: 'veterinarian'
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Step 2: Create vet profile
        const vetResponse = await fetch(`${API_URL}/api/veterinarians`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${data.access_token}`
          },
          
          body: JSON.stringify({
            user_id: data.user.user_id,
            clinic_name: registerForm.clinicName,
            address: registerForm.address,
            city: registerForm.city,
            postal_code: registerForm.postalCode,
            phone: registerForm.phone,
            selected_plan: registerForm.selectedPlan
          })
        });
        
        if (vetResponse.ok) {
          localStorage.setItem('vetToken', data.access_token);
          
          // Step 3: Create Stripe checkout session and redirect to add CB
          toast.success('Inscription réussie ! Redirection vers le paiement...');
          
          const stripeResponse = await fetch(`${API_URL}/api/subscriptions/checkout`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${data.access_token}`
            },
            
            body: JSON.stringify({
              origin_url: window.location.origin,
              plan: registerForm.selectedPlan
            })
          });
          
          const stripeData = await stripeResponse.json();
          
          if (stripeResponse.ok && stripeData.checkout_url) {
            // Redirect to Stripe Checkout to add CB
            window.location.href = stripeData.checkout_url;
          } else {
            // If Stripe fails, still log in but show warning
            console.error('Stripe error:', stripeData);
            const vetData = await vetResponse.json().catch(() => ({}));
            setVetProfile({ ...vetData, status: 'pending' });
            setIsLoggedIn(true);
            toast.error('Erreur lors de la création du paiement. Vous pourrez ajouter votre CB plus tard.');
          }
        } else {
          const vetError = await vetResponse.json();
          console.error('Vet profile error:', vetError);
          toast.error(vetError.detail || 'Erreur lors de la création du profil vétérinaire');
        }
      } else {
        toast.error(data.detail || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Erreur de connexion au serveur. Veuillez réessayer.');
    }
    setIsLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('vetToken');
    setIsLoggedIn(false);
    setVetProfile(null);
    toast.success('Déconnexion réussie');
  };

  // Handle Stripe payment
  const handleStartPayment = async (planId = null) => {
    const plan = planId || vetProfile?.selected_plan || selectedPlan;
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('vetToken');
      const response = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        
        body: JSON.stringify({
          origin_url: window.location.origin,
          plan: plan
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || 'Erreur lors de la création du paiement');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Erreur de connexion au serveur');
    }
    setIsLoading(false);
  };

  // Render pending approval message
  const renderPendingApproval = () => (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-amber-50 to-orange-100">
      <Header />
      <main className="flex-grow flex items-center justify-center py-12 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <HourglassIcon className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Inscription en cours de validation</h2>
          <p className="text-gray-600 mb-6">
            Votre demande d&apos;inscription a bien été reçue. Notre équipe examine votre dossier et vous recevrez une confirmation par email sous 24 à 48 heures.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              <strong>Offre sélectionnée :</strong> {currentPlan.name} - {currentPlan.price}€/mois
            </p>
            <p className="text-xs text-amber-600 mt-1">
              Les 60 premiers jours sont offerts après validation.
            </p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="w-full">
            Se déconnecter
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );

  // Render dashboard for approved vets
  const renderDashboard = () => (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-grow py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{vetProfile?.clinic_name || 'Mon Cabinet'}</h1>
                <p className="text-gray-500">Tableau de bord vétérinaire</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-gray-500">
                    {vetProfile?.appointments_this_month || 0}/{currentPlan.appointmentsLimit} RDV ce mois
                  </p>
                  <p className="text-xs text-green-600">Offre {currentPlan.name}: {currentPlan.price}€/mois</p>
                </div>
                <Button onClick={handleLogout} variant="outline" size="sm">Se déconnecter</Button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_this_month}</p>
                  <p className="text-xs text-gray-500">RDV ce mois</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.unique_patients}</p>
                  <p className="text-xs text-gray-500">Patients</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.completion_rate}%</p>
                  <p className="text-xs text-gray-500">Taux de présence</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{currentPlan.appointmentsLimit - (vetProfile?.appointments_this_month || 0)}</p>
                  <p className="text-xs text-gray-500">RDV restants</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="border-b">
              <nav className="flex gap-4 px-6 overflow-x-auto">
                <button 
                  onClick={() => setDashboardTab('appointments')}
                  className={`py-4 border-b-2 font-medium text-sm whitespace-nowrap ${dashboardTab === 'appointments' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}
                  data-testid="tab-appointments"
                >
                  Rendez-vous
                </button>
                <button 
                  onClick={() => setDashboardTab('agenda')}
                  className={`py-4 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${dashboardTab === 'agenda' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}
                  data-testid="tab-agenda"
                >
                  <CalendarDays className="w-4 h-4" />
                  Agenda
                  {(vetProfile?.subscription_plan === 'pro' || vetProfile?.subscription_plan === 'multi') && (
                    <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded">PRO</span>
                  )}
                </button>
                <button 
                  onClick={() => setDashboardTab('patients')}
                  className={`py-4 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${dashboardTab === 'patients' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}
                  data-testid="tab-patients"
                >
                  <PawPrint className="w-4 h-4" />
                  Carnets partagés
                  {sharedAnimals.length > 0 && (
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">{sharedAnimals.length}</span>
                  )}
                </button>
                <button 
                  onClick={() => setDashboardTab('schedule')}
                  className={`py-4 border-b-2 font-medium text-sm whitespace-nowrap ${dashboardTab === 'schedule' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}
                  data-testid="tab-schedule"
                >
                  Horaires
                </button>
                <button 
                  onClick={() => setDashboardTab('invoices')}
                  className={`py-4 border-b-2 font-medium text-sm whitespace-nowrap ${dashboardTab === 'invoices' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}
                  data-testid="tab-invoices"
                >
                  Factures
                </button>
                <button 
                  onClick={() => setDashboardTab('subscription')}
                  className={`py-4 border-b-2 font-medium text-sm whitespace-nowrap ${dashboardTab === 'subscription' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}
                  data-testid="tab-subscription"
                >
                  Abonnement
                </button>
                <button 
                  onClick={() => setDashboardTab('integration')}
                  className={`py-4 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${dashboardTab === 'integration' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}
                  data-testid="tab-integration"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Intégration
                </button>
                <button 
                  onClick={() => setDashboardTab('profile')}
                  className={`py-4 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${dashboardTab === 'profile' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}
                  data-testid="tab-profile"
                >
                  <User className="w-4 h-4" />
                  Mon Profil
                </button>
                <button 
                  onClick={() => setDashboardTab('help')}
                  className={`py-4 border-b-2 font-medium text-sm whitespace-nowrap ${dashboardTab === 'help' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}
                  data-testid="tab-help"
                >
                  Aide
                </button>
              </nav>
            </div>

            <div className="p-6">
              {dashboardTab === 'appointments' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Rendez-vous</h3>
                    <div className="flex gap-2">
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                        {stats.pending} en attente
                      </span>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        {stats.confirmed} confirmés
                      </span>
                    </div>
                  </div>
                  
                  {/* Quick Teleconsultation Button */}
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-blue-800 flex items-center gap-2">
                          <Video className="w-5 h-5" />
                          Démarrer une téléconsultation rapide
                        </p>
                        <p className="text-sm text-blue-600 mt-1">
                          Sans rendez-vous, pour une consultation immédiate
                        </p>
                      </div>
                      <Button
                        onClick={() => {
                          setTeleconsultationData({
                            appointmentId: '',
                            patientName: 'Patient',
                            animalName: 'Animal',
                            reason: 'Téléconsultation rapide'
                          });
                          setShowTeleconsultation(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                        data-testid="quick-teleconsult-btn"
                      >
                        <Video className="w-4 h-4" />
                        Démarrer
                      </Button>
                    </div>
                  </div>
                  
                  {/* Google Calendar Notice */}
                  {!googleCalendar.connected && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-blue-800">Synchronisez vos rendez-vous avec Google Agenda</p>
                        <p className="text-sm text-blue-600 mt-1">
                          Pour une meilleure gestion de vos rendez-vous, connectez votre Google Agenda. 
                          Vos RDV seront automatiquement ajoutés à votre calendrier.
                        </p>
                        <Button
                          onClick={connectGoogleCalendar}
                          disabled={loadingGoogle}
                          className="mt-3 bg-white border border-blue-300 text-blue-700 hover:bg-blue-50 flex items-center gap-2"
                          size="sm"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Connecter Google Agenda
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {loadingData ? (
                    <div className="text-center py-8">
                      <Clock className="w-8 h-8 animate-spin text-green-500 mx-auto mb-3" />
                      <p className="text-gray-500">Chargement...</p>
                    </div>
                  ) : appointments.length > 0 ? (
                    <div className="space-y-3">
                      {appointments.map((appt) => (
                        <div key={appt.appointment_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              appt.status === 'confirmed' ? 'bg-green-100' :
                              appt.status === 'pending' ? 'bg-orange-100' :
                              appt.status === 'completed' ? 'bg-blue-100' : 'bg-gray-100'
                            }`}>
                              <Clock className={`w-5 h-5 ${
                                appt.status === 'confirmed' ? 'text-green-600' :
                                appt.status === 'pending' ? 'text-orange-600' :
                                appt.status === 'completed' ? 'text-blue-600' : 'text-gray-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium">{appt.patient_name || 'Patient'}</p>
                              <p className="text-sm text-gray-500">{appt.animal_name} ({appt.animal_type}) - {appt.appointment_type}</p>
                              {appt.reason && <p className="text-xs text-gray-400">{appt.reason}</p>}
                            </div>
                          </div>
                          <div className="text-right flex flex-col items-end gap-2">
                            <p className="font-medium text-green-600">{appt.time}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(appt.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </p>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                appt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                appt.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                                appt.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {appt.status === 'confirmed' ? 'Confirmé' :
                                 appt.status === 'pending' ? 'En attente' :
                                 appt.status === 'completed' ? 'Terminé' : appt.status}
                              </span>
                              {(appt.status === 'confirmed' || appt.status === 'pending') && (
                                ['pro', 'multi'].includes(vetProfile?.subscription_plan) ? (
                                  <Button
                                    size="sm"
                                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 h-auto"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const data = {
                                        appointmentId: appt.appointment_id,
                                        patientName: appt.patient_name,
                                        animalName: appt.animal_name,
                                        reason: appt.reason
                                      };
                                      setTeleconsultationData(data);
                                      setTimeout(() => {
                                        setShowTeleconsultation(true);
                                      }, 100);
                                    }}
                                    data-testid={`teleconsult-btn-${appt.appointment_id}`}
                                  >
                                    <Video className="w-3 h-3 mr-1" />
                                    Téléconsult
                                  </Button>
                                ) : (
                                  <span 
                                    className="text-xs text-gray-400 flex items-center gap-1 cursor-help"
                                    title="Disponible avec le forfait Pro ou Multi"
                                  >
                                    <Video className="w-3 h-3" />
                                    Pro
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Aucun rendez-vous</p>
                      <p className="text-sm">Les rendez-vous pris par vos patients apparaîtront ici.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Agenda Tab - Calendar View */}
              {dashboardTab === 'agenda' && (
                <div data-testid="agenda-content">
                  <div className="mb-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <CalendarDays className="w-5 h-5 text-green-600" />
                      Agenda Complet
                    </h3>
                    <p className="text-sm text-gray-500">
                      {(vetProfile?.subscription_plan === 'pro' || vetProfile?.subscription_plan === 'multi') 
                        ? 'Vue semaine/mois avec créneaux récurrents' 
                        : 'Passez au forfait Pro pour accéder aux vues avancées'}
                    </p>
                  </div>
                  
                  <AgendaCalendar
                    appointments={appointments}
                    schedule={schedule}
                    subscriptionPlan={vetProfile?.subscription_plan || 'essentiel'}
                    recurringSlots={recurringSlots}
                    onAppointmentClick={(apt) => {
                      toast.info(`RDV: ${apt.patient_name} - ${apt.animal_name}`);
                    }}
                    onSlotClick={(slot) => {
                      toast.info(`Créneau sélectionné: ${slot.time}`);
                    }}
                    onRecurringSlotSave={() => {
                      fetchRecurringSlots();
                    }}
                  />
                  
                  {/* Recurring slots list for Pro/Multi */}
                  {(vetProfile?.subscription_plan === 'pro' || vetProfile?.subscription_plan === 'multi') && recurringSlots.length > 0 && (
                    <div className="mt-6 bg-blue-50 rounded-xl p-4">
                      <h4 className="font-medium text-blue-800 flex items-center gap-2 mb-3">
                        <Repeat className="w-4 h-4" />
                        Créneaux récurrents actifs ({recurringSlots.length})
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {recurringSlots.map(slot => (
                          <div key={slot.slot_id} className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm">
                                {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'][slot.day_of_week]}
                              </span>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                {slot.slot_type}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {slot.start_time} - {slot.end_time}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Shared Animals Tab */}
              {dashboardTab === 'patients' && (
                <div data-testid="patients-content">
                  {selectedSharedAnimal ? (
                    // Animal detail view
                    <div className="space-y-6">
                      <div className="flex items-center gap-4">
                        <button 
                          onClick={() => {
                            setSelectedSharedAnimal(null);
                            setSharedAnimalRecords([]);
                          }}
                          className="p-2 hover:bg-gray-100 rounded-full"
                        >
                          <X className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center text-2xl">
                            {selectedSharedAnimal.species === 'chien' ? '🐕' : 
                             selectedSharedAnimal.species === 'chat' ? '🐱' : '🐾'}
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold">{selectedSharedAnimal.name}</h3>
                            <p className="text-sm text-gray-500">
                              {selectedSharedAnimal.species} {selectedSharedAnimal.breed && `• ${selectedSharedAnimal.breed}`}
                              {' • Propriétaire: '}{selectedSharedAnimal.owner_name}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Animal info */}
                      <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {selectedSharedAnimal.weight && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Poids</p>
                            <p className="font-medium">{selectedSharedAnimal.weight} kg</p>
                          </div>
                        )}
                        {selectedSharedAnimal.birth_date && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Naissance</p>
                            <p className="font-medium">{new Date(selectedSharedAnimal.birth_date).toLocaleDateString('fr-FR')}</p>
                          </div>
                        )}
                        {selectedSharedAnimal.identification_number && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Identification</p>
                            <p className="font-medium text-sm">{selectedSharedAnimal.identification_number}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Stérilisé</p>
                          <p className="font-medium">{selectedSharedAnimal.sterilized ? 'Oui' : 'Non'}</p>
                        </div>
                      </div>

                      {/* Add record button */}
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold">Historique médical</h4>
                        <Button 
                          onClick={() => setShowAddRecordForm(true)}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Ajouter un événement
                        </Button>
                      </div>

                      {/* Records timeline */}
                      {sharedAnimalRecords.length > 0 ? (
                        <div className="space-y-3">
                          {sharedAnimalRecords.map((record) => {
                            const config = RECORD_TYPES[record.record_type] || RECORD_TYPES.consultation;
                            const Icon = config.icon;
                            return (
                              <div 
                                key={record.record_id}
                                className={`p-4 rounded-xl ${config.bgLight} border border-gray-100`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 ${config.color} rounded-lg text-white`}>
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <h5 className="font-semibold text-gray-800">{record.title}</h5>
                                        <p className="text-sm text-gray-500">
                                          {new Date(record.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                          {record.vet_name && ` • ${record.vet_name}`}
                                        </p>
                                      </div>
                                      <span className={`text-xs px-2 py-1 rounded-full ${config.bgLight} ${config.textColor}`}>
                                        {config.label}
                                      </span>
                                    </div>
                                    {record.description && (
                                      <p className="text-sm text-gray-600 mt-2">{record.description}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-xl">
                          <FileText className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                          <p className="text-gray-500">Aucun événement médical</p>
                        </div>
                      )}

                      {/* Add Record Form Modal */}
                      {showAddRecordForm && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowAddRecordForm(false)}>
                          <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
                            <div className="p-6 border-b">
                              <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold">Ajouter un événement médical</h3>
                                <button onClick={() => setShowAddRecordForm(false)} className="p-2 hover:bg-gray-100 rounded-full">
                                  <X className="w-5 h-5" />
                                </button>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">Pour {selectedSharedAnimal.name}</p>
                            </div>
                            <form onSubmit={handleAddRecord} className="p-6 space-y-4">
                              <div>
                                <Label>Type d'événement</Label>
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                  {Object.entries(RECORD_TYPES).map(([type, config]) => {
                                    const Icon = config.icon;
                                    return (
                                      <button
                                        key={type}
                                        type="button"
                                        onClick={() => setNewRecordForm({ ...newRecordForm, record_type: type })}
                                        className={`p-2 rounded-lg border-2 text-center transition-all ${
                                          newRecordForm.record_type === type 
                                            ? `border-green-500 ${config.bgLight}` 
                                            : 'border-gray-200'
                                        }`}
                                      >
                                        <Icon className={`w-4 h-4 mx-auto mb-1 ${config.textColor}`} />
                                        <span className="text-xs">{config.label}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              <div>
                                <Label htmlFor="record-title">Titre *</Label>
                                <Input
                                  id="record-title"
                                  value={newRecordForm.title}
                                  onChange={(e) => setNewRecordForm({ ...newRecordForm, title: e.target.value })}
                                  placeholder="Ex: Consultation de suivi"
                                  required
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label htmlFor="record-date">Date *</Label>
                                <Input
                                  id="record-date"
                                  type="date"
                                  value={newRecordForm.date}
                                  onChange={(e) => setNewRecordForm({ ...newRecordForm, date: e.target.value })}
                                  required
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label htmlFor="record-desc">Description</Label>
                                <textarea
                                  id="record-desc"
                                  value={newRecordForm.description}
                                  onChange={(e) => setNewRecordForm({ ...newRecordForm, description: e.target.value })}
                                  placeholder="Notes, observations..."
                                  className="mt-1 w-full p-3 border rounded-lg resize-none h-20"
                                />
                              </div>
                              <Button type="submit" className="w-full bg-green-500 hover:bg-green-600 text-white">
                                <Plus className="w-4 h-4 mr-2" />
                                Ajouter au carnet
                              </Button>
                            </form>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Animals list
                    <div>
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="font-semibold">Carnets de santé partagés</h3>
                          <p className="text-sm text-gray-500">Animaux dont les propriétaires vous ont donné accès</p>
                        </div>
                        <Button variant="outline" onClick={fetchSharedAnimals} size="sm">
                          Actualiser
                        </Button>
                      </div>

                      {sharedAnimals.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {sharedAnimals.map((animal) => (
                            <div 
                              key={animal.animal_id}
                              onClick={() => {
                                setSelectedSharedAnimal(animal);
                                fetchAnimalRecords(animal.animal_id);
                              }}
                              className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer hover:border-green-300 hover:shadow-md transition-all"
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center text-3xl">
                                  {animal.species === 'chien' ? '🐕' : 
                                   animal.species === 'chat' ? '🐱' : 
                                   animal.species === 'lapin' ? '🐰' : '🐾'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-gray-800">{animal.name}</h4>
                                  <p className="text-sm text-gray-500">
                                    {animal.species} {animal.breed && `• ${animal.breed}`}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {animal.owner_name}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                                    {animal.records_count || 0} événements
                                  </span>
                                  <ChevronRight className="w-5 h-5 text-gray-400 mt-2 ml-auto" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 bg-gray-50 rounded-xl">
                          <PawPrint className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                          <h4 className="font-medium text-gray-800 mb-2">Aucun carnet partagé</h4>
                          <p className="text-sm text-gray-500 max-w-sm mx-auto">
                            Lorsqu'un propriétaire vous partagera l'accès au carnet de santé de son animal, il apparaîtra ici.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Schedule Tab */}
              {dashboardTab === 'schedule' && (
                <div data-testid="schedule-content">
                  <h3 className="font-semibold mb-4">Configuration des horaires</h3>
                  
                  {loadingSchedule ? (
                    <div className="text-center py-8">
                      <Clock className="w-8 h-8 animate-spin text-green-500 mx-auto mb-3" />
                      <p className="text-gray-500">Chargement...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Slot Duration */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <Label className="text-sm font-medium">Durée des créneaux</Label>
                        <div className="flex gap-2 mt-2">
                          {[15, 30, 45, 60].map(duration => (
                            <button
                              key={duration}
                              onClick={() => updateSchedule({ slot_duration: duration })}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                (schedule?.slot_duration || 30) === duration
                                  ? 'bg-green-500 text-white'
                                  : 'bg-white border border-gray-300 text-gray-700 hover:border-green-500'
                              }`}
                              data-testid={`slot-duration-${duration}`}
                            >
                              {duration} min
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Weekly Schedule */}
                      <div>
                        <h4 className="font-medium mb-3">Horaires hebdomadaires</h4>
                        <div className="space-y-3">
                          {(schedule?.weekly_schedule || [
                            { day: 0, day_name: "Lundi", is_open: true, start_time: "09:00", end_time: "18:00", break_start: "12:00", break_end: "14:00" },
                            { day: 1, day_name: "Mardi", is_open: true, start_time: "09:00", end_time: "18:00", break_start: "12:00", break_end: "14:00" },
                            { day: 2, day_name: "Mercredi", is_open: true, start_time: "09:00", end_time: "18:00", break_start: "12:00", break_end: "14:00" },
                            { day: 3, day_name: "Jeudi", is_open: true, start_time: "09:00", end_time: "18:00", break_start: "12:00", break_end: "14:00" },
                            { day: 4, day_name: "Vendredi", is_open: true, start_time: "09:00", end_time: "18:00", break_start: "12:00", break_end: "14:00" },
                            { day: 5, day_name: "Samedi", is_open: true, start_time: "09:00", end_time: "12:00", break_start: null, break_end: null },
                            { day: 6, day_name: "Dimanche", is_open: false, start_time: "09:00", end_time: "18:00", break_start: null, break_end: null }
                          ]).map((day, idx) => (
                            <div key={idx} className="bg-white border rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <Switch
                                    checked={day.is_open}
                                    onCheckedChange={(checked) => {
                                      const newSchedule = [...(schedule?.weekly_schedule || [])];
                                      newSchedule[idx] = { ...day, is_open: checked };
                                      updateSchedule({ weekly_schedule: newSchedule });
                                    }}
                                    data-testid={`day-switch-${idx}`}
                                  />
                                  <span className={`font-medium ${day.is_open ? 'text-gray-800' : 'text-gray-400'}`}>
                                    {day.day_name}
                                  </span>
                                </div>
                                {!day.is_open && (
                                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">Fermé</span>
                                )}
                              </div>
                              
                              {day.is_open && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div>
                                    <Label className="text-xs text-gray-500">Ouverture</Label>
                                    <Input
                                      type="time"
                                      value={day.start_time || "09:00"}
                                      onChange={(e) => {
                                        const newSchedule = [...(schedule?.weekly_schedule || [])];
                                        newSchedule[idx] = { ...day, start_time: e.target.value };
                                        updateSchedule({ weekly_schedule: newSchedule });
                                      }}
                                      className="mt-1"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-gray-500">Fermeture</Label>
                                    <Input
                                      type="time"
                                      value={day.end_time || "18:00"}
                                      onChange={(e) => {
                                        const newSchedule = [...(schedule?.weekly_schedule || [])];
                                        newSchedule[idx] = { ...day, end_time: e.target.value };
                                        updateSchedule({ weekly_schedule: newSchedule });
                                      }}
                                      className="mt-1"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-gray-500">Pause début</Label>
                                    <Input
                                      type="time"
                                      value={day.break_start || ""}
                                      onChange={(e) => {
                                        const newSchedule = [...(schedule?.weekly_schedule || [])];
                                        newSchedule[idx] = { ...day, break_start: e.target.value || null };
                                        updateSchedule({ weekly_schedule: newSchedule });
                                      }}
                                      className="mt-1"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-gray-500">Pause fin</Label>
                                    <Input
                                      type="time"
                                      value={day.break_end || ""}
                                      onChange={(e) => {
                                        const newSchedule = [...(schedule?.weekly_schedule || [])];
                                        newSchedule[idx] = { ...day, break_end: e.target.value || null };
                                        updateSchedule({ weekly_schedule: newSchedule });
                                      }}
                                      className="mt-1"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Blocked Dates */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <CalendarX className="w-4 h-4" />
                          Jours de fermeture exceptionnelle
                        </h4>
                        
                        <div className="flex gap-2 mb-4">
                          <Input
                            type="date"
                            value={newBlockedDate}
                            onChange={(e) => setNewBlockedDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="flex-1"
                            data-testid="blocked-date-input"
                          />
                          <Button
                            onClick={addBlockedDate}
                            disabled={!newBlockedDate}
                            className="bg-green-500 hover:bg-green-600 text-white"
                            data-testid="add-blocked-date-btn"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {(schedule?.blocked_dates || []).length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {schedule.blocked_dates.map((date, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 bg-white border rounded-full px-3 py-1"
                              >
                                <span className="text-sm">
                                  {new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                                <button
                                  onClick={() => removeBlockedDate(date)}
                                  className="text-gray-400 hover:text-red-500"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Aucun jour bloqué</p>
                        )}
                      </div>

                      {/* Google Calendar Integration */}
                      <div className="bg-gray-50 rounded-lg p-4 mt-6">
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Synchronisation Google Agenda
                        </h4>
                        
                        {googleCalendar.connected ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-green-600">
                              <Check className="w-5 h-5" />
                              <span>Connecté à : <strong>{googleCalendar.email}</strong></span>
                            </div>
                            <p className="text-sm text-gray-500">
                              Les nouveaux rendez-vous seront automatiquement ajoutés à votre Google Agenda.
                            </p>
                            <div className="flex gap-2">
                              <Button
                                onClick={syncAllToGoogle}
                                disabled={loadingGoogle}
                                variant="outline"
                                className="flex items-center gap-2"
                              >
                                <Calendar className="w-4 h-4" />
                                Synchroniser tous les RDV
                              </Button>
                              <Button
                                onClick={disconnectGoogleCalendar}
                                variant="outline"
                                className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
                              >
                                <Unlink className="w-4 h-4" />
                                Déconnecter
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-500">
                              Connectez votre Google Agenda pour synchroniser automatiquement vos rendez-vous.
                            </p>
                            <Button
                              onClick={connectGoogleCalendar}
                              disabled={loadingGoogle}
                              className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                              </svg>
                              Connecter Google Agenda
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Invoices Tab */}
              {dashboardTab === 'invoices' && (
                <div data-testid="invoices-content">
                  <h3 className="font-semibold mb-4">Mes factures</h3>
                  
                  {loadingInvoices ? (
                    <div className="text-center py-8">
                      <Clock className="w-8 h-8 animate-spin text-green-500 mx-auto mb-3" />
                      <p className="text-gray-500">Chargement...</p>
                    </div>
                  ) : invoices.length > 0 ? (
                    <div className="space-y-3">
                      {invoices.map((invoice) => (
                        <div
                          key={invoice.invoice_id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          data-testid={`invoice-${invoice.invoice_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                              <FileText className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium">{invoice.invoice_number}</p>
                              <p className="text-sm text-gray-500">
                                {new Date(invoice.invoice_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </p>
                              <p className="text-xs text-gray-400">
                                Plan {invoice.subscription_plan?.charAt(0).toUpperCase() + invoice.subscription_plan?.slice(1)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-bold text-gray-800">{invoice.total?.toFixed(2)} €</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                                invoice.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {invoice.status === 'paid' ? 'Payée' : 
                                 invoice.status === 'pending' ? 'En attente' : invoice.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => previewInvoice(invoice.invoice_id)}
                                className="flex items-center gap-1 text-gray-600 hover:text-gray-800"
                                title="Aperçu"
                                data-testid={`preview-invoice-${invoice.invoice_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadInvoice(invoice.invoice_id, invoice.invoice_number)}
                                className="flex items-center gap-1 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                                data-testid={`download-invoice-${invoice.invoice_id}`}
                              >
                                <Download className="w-4 h-4" />
                                PDF
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Aucune facture</p>
                      <p className="text-sm">Vos factures apparaîtront ici après chaque paiement.</p>
                    </div>
                  )}
                </div>
              )}

              {dashboardTab === 'subscription' && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      vetProfile?.subscription_status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : vetProfile?.subscription_status === 'trial'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {vetProfile?.subscription_status === 'active' ? '✓ Actif' : 
                       vetProfile?.subscription_status === 'trial' ? '🎁 Essai gratuit (60 jours)' : '⏳ En attente de paiement'}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <h4 className="font-semibold text-lg mb-2">Offre {currentPlan.name}</h4>
                    <p className="text-3xl font-bold text-gray-800 mb-4">{currentPlan.price}€<span className="text-base font-normal text-gray-500">/mois</span></p>
                    
                    <ul className="space-y-2">
                      {currentPlan.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {vetProfile?.subscription_status === 'trial' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-blue-800">
                        <strong>Période d&apos;essai :</strong> Il vous reste {vetProfile?.trial_days_left || 60} jours d&apos;essai gratuit.
                      </p>
                    </div>
                  )}

                  {(!vetProfile?.subscription_status || vetProfile?.subscription_status === 'inactive') && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-amber-800 mb-3">
                        <strong>Activez votre abonnement</strong> pour débloquer toutes les fonctionnalités.
                      </p>
                      <Button 
                        onClick={handleStartPayment}
                        disabled={isLoading}
                        className="bg-green-500 hover:bg-green-600 text-white w-full"
                      >
                        {isLoading ? 'Chargement...' : `Payer ${currentPlan.price}€/mois`}
                      </Button>
                    </div>
                  )}

                  <div className="flex gap-3">
                    {vetProfile?.subscription_status === 'active' && (
                      <Button className="bg-green-500 hover:bg-green-600 text-white">
                        Gérer l'abonnement
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => setShowPricingModal(true)}>
                      Changer d'offre
                    </Button>
                  </div>
                </div>
              )}

              {/* Integration Tab */}
              {dashboardTab === 'integration' && (
                <IntegrationManager />
              )}

              {/* Profile Tab */}
              {dashboardTab === 'profile' && (
                <div className="bg-white rounded-xl p-6" data-testid="profile-content">
                  <VetProfileEditor 
                    token={localStorage.getItem('vetToken')} 
                    onUpdate={(updated) => setVetProfile({...vetProfile, ...updated})}
                  />
                </div>
              )}

              {/* Help Tab */}
              {dashboardTab === 'help' && (
                <div className="space-y-6" data-testid="help-content">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-800">Guide d'utilisation</h2>
                    <p className="text-gray-500">Tout ce que vous devez savoir pour utiliser vetoRDV</p>
                  </div>

                  {/* Section 1: Premiers pas */}
                  <div className="bg-white border rounded-xl overflow-hidden">
                    <div className="bg-green-50 px-6 py-4 border-b">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm">1</span>
                        Premiers pas
                      </h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Stethoscope className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Créer votre compte</h4>
                          <p className="text-sm text-gray-500">Inscrivez-vous avec les informations de votre clinique. Votre compte sera validé par notre équipe sous 24-48h.</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Connecter Google Agenda</h4>
                          <p className="text-sm text-gray-500">
                            <strong className="text-blue-600">Obligatoire :</strong> Allez dans l'onglet "Horaires" et cliquez sur "Connecter Google Agenda". 
                            Vos rendez-vous seront automatiquement synchronisés.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Clock className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Configurer vos horaires</h4>
                          <p className="text-sm text-gray-500">Dans l'onglet "Horaires", définissez vos jours et heures de travail, vos pauses, et la durée de vos consultations.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Gestion des RDV */}
                  <div className="bg-white border rounded-xl overflow-hidden">
                    <div className="bg-blue-50 px-6 py-4 border-b">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm">2</span>
                        Gestion des rendez-vous
                      </h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CalendarDays className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Voir vos rendez-vous</h4>
                          <p className="text-sm text-gray-500">L'onglet "Rendez-vous" affiche tous vos RDV à venir avec le nom du patient, l'animal, et le motif de consultation.</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Bell className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Rappels automatiques</h4>
                          <p className="text-sm text-gray-500">Vos patients reçoivent automatiquement un email de rappel 24h avant leur rendez-vous.</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Link2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Synchronisation Google Agenda</h4>
                          <p className="text-sm text-gray-500">Chaque nouveau RDV est automatiquement ajouté à votre Google Agenda. Vous pouvez aussi cliquer sur "Synchroniser tous les RDV" dans l'onglet Horaires.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Configuration du planning */}
                  <div className="bg-white border rounded-xl overflow-hidden">
                    <div className="bg-purple-50 px-6 py-4 border-b">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm">3</span>
                        Configuration du planning
                      </h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Clock className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Horaires d'ouverture</h4>
                          <p className="text-sm text-gray-500">Définissez pour chaque jour : heure d'ouverture, heure de fermeture, et pause déjeuner. Désactivez les jours où vous ne travaillez pas.</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CalendarX className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Jours de fermeture</h4>
                          <p className="text-sm text-gray-500">Ajoutez des dates spécifiques où vous serez absent (vacances, congés, formation). Les patients ne pourront pas réserver ces jours-là.</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Settings className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Durée des créneaux</h4>
                          <p className="text-sm text-gray-500">Choisissez la durée de vos consultations : 15, 30, 45 ou 60 minutes. Les créneaux proposés aux patients seront adaptés.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Facturation */}
                  <div className="bg-white border rounded-xl overflow-hidden">
                    <div className="bg-amber-50 px-6 py-4 border-b">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm">4</span>
                        Facturation
                      </h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Consulter vos factures</h4>
                          <p className="text-sm text-gray-500">L'onglet "Factures" affiche l'historique de vos paiements d'abonnement avec la date, le montant et le statut.</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Download className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Télécharger une facture</h4>
                          <p className="text-sm text-gray-500">Cliquez sur le bouton "PDF" à côté de chaque facture pour la télécharger ou l'imprimer.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 5: Abonnement */}
                  <div className="bg-white border rounded-xl overflow-hidden">
                    <div className="bg-green-50 px-6 py-4 border-b">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm">5</span>
                        Abonnement
                      </h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CreditCard className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Choisir votre offre</h4>
                          <p className="text-sm text-gray-500">3 offres disponibles : Essentiel (29€), Pro (49€), Multi (79€). Chaque offre inclut un nombre différent de RDV mensuels.</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Check className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Essai gratuit</h4>
                          <p className="text-sm text-gray-500">Profitez de 60 jours d'essai gratuit pour tester toutes les fonctionnalités. Votre carte bancaire est enregistrée mais ne sera débitée qu'après la période d'essai.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Support */}
                  <div className="bg-gray-50 rounded-xl p-6 text-center">
                    <h4 className="font-semibold mb-2">Besoin d'aide supplémentaire ?</h4>
                    <p className="text-sm text-gray-500 mb-4">Notre équipe est là pour vous accompagner</p>
                    <Button variant="outline" className="gap-2">
                      <Mail className="w-4 h-4" />
                      Contacter le support
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      
      {/* Teleconsultation Modal - Must be inside renderDashboard */}
      {showTeleconsultation && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4" style={{zIndex: 9999}}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto shadow-2xl">
            <div className="p-4 border-b flex items-center justify-between bg-blue-50">
              <h2 className="text-xl font-bold flex items-center gap-2 text-blue-800">
                <Video className="w-6 h-6 text-blue-600" />
                Téléconsultation
              </h2>
              <button
                onClick={() => {
                  setShowTeleconsultation(false);
                  setTeleconsultationData(null);
                }}
                className="p-2 hover:bg-blue-100 rounded-full transition-colors"
                data-testid="close-teleconsultation-modal"
              >
                <X className="w-5 h-5 text-blue-800" />
              </button>
            </div>
            <div className="p-6">
              <Teleconsultation
                appointmentId={teleconsultationData?.appointmentId}
                patientName={teleconsultationData?.patientName}
                animalName={teleconsultationData?.animalName}
                reason={teleconsultationData?.reason}
                onClose={() => {
                  setShowTeleconsultation(false);
                  setTeleconsultationData(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );

  // If logged in and pending approval
  if (isLoggedIn && vetProfile?.status === 'pending') {
    return renderPendingApproval();
  }

  // If logged in and approved
  if (isLoggedIn && vetProfile?.status === 'approved') {
    return renderDashboard();
  }

  // If logged in but no specific status (treat as approved for now)
  if (isLoggedIn) {
    return renderDashboard();
  }

  // Not logged in - show landing page
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
      <Header />
      
      <main className="flex-grow">
        {/* Promo Banner */}
        <PromoBanner variant="highlighted" />
        
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 py-16 px-4">
          <div className="max-w-6xl mx-auto">
            {/* Promo Banner inside hero for visibility */}
            <div className="mb-8">
              <PromoBanner variant="highlighted" />
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-800 dark:text-white mb-6">
                  Gérez vos rendez-vous<br />
                  <span className="text-green-600">simplement</span>
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                  Rejoignez vetoRDV et offrez à vos patients la possibilité de prendre rendez-vous en ligne 24h/24.
                </p>
                <div className="flex gap-4 flex-wrap">
                  <Button 
                    onClick={() => setActiveTab('register')}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg"
                    data-testid="cta-trial-button"
                  >
                    🎁 Profiter des 2 mois offerts
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('login')}
                    variant="outline"
                    className="px-8 py-6 dark:border-gray-600 dark:text-gray-200"
                  >
                    Se connecter
                  </Button>
                </div>
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                  💳 CB requise à l'inscription • Premier prélèvement dans 60 jours
                </p>
              </div>

              {/* Pricing Cards or Login/Register */}
              {activeTab === 'info' && (
                <div className="space-y-6">
                  {/* Quick plan selector */}
                  <div className="grid grid-cols-3 gap-3">
                    {pricingPlans.filter(p => p.active).map((plan) => (
                      <div 
                        key={plan.id}
                        className={`bg-white rounded-xl shadow-md p-4 relative cursor-pointer transition-all hover:shadow-lg ${
                          plan.popular ? 'ring-2 ring-blue-500' : ''
                        } ${selectedPlan === plan.id ? 'ring-2 ring-green-500 bg-green-50' : ''}`}
                        onClick={() => setSelectedPlan(plan.id)}
                      >
                        {plan.popular && (
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            POPULAIRE
                          </div>
                        )}
                        <h3 className={`font-bold text-center ${
                          plan.color === 'green' ? 'text-green-600' : 
                          plan.color === 'blue' ? 'text-blue-600' : 'text-purple-600'
                        }`}>
                          {plan.name}
                        </h3>
                        <p className="text-center mt-1">
                          <span className={`text-2xl font-bold ${
                            plan.color === 'green' ? 'text-green-600' : 
                            plan.color === 'blue' ? 'text-blue-600' : 'text-purple-600'
                          }`}>{plan.price}€</span>
                          <span className="text-gray-500 text-sm">/mois</span>
                        </p>
                        <p className="text-xs text-gray-500 text-center mt-1">{plan.appointmentsLimit} RDV/mois</p>
                        {selectedPlan === plan.id && (
                          <div className="text-center mt-2">
                            <span className="text-green-600 text-xs font-semibold">✓ Sélectionné</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Key differences summary */}
                  <div className="bg-white rounded-xl p-4 shadow-md">
                    <h4 className="font-semibold text-gray-800 mb-3 text-sm">Fonctionnalités clés :</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">📹 Téléconsultation vidéo</span>
                        <div className="flex gap-3">
                          <span className="text-gray-300">Essentiel ✗</span>
                          <span className="text-blue-600 font-medium">Pro ✓</span>
                          <span className="text-purple-600 font-medium">Multi ✓</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">📱 Rappels SMS</span>
                        <div className="flex gap-3">
                          <span className="text-gray-300">Essentiel ✗</span>
                          <span className="text-blue-600 font-medium">Pro ✓</span>
                          <span className="text-purple-600 font-medium">Multi ✓</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">👥 Vétérinaires</span>
                        <div className="flex gap-3">
                          <span className="text-green-600">1</span>
                          <span className="text-blue-600 font-medium">2</span>
                          <span className="text-purple-600 font-medium">5</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-1">
                        <span className="text-gray-600">📊 Stats avancées</span>
                        <div className="flex gap-3">
                          <span className="text-gray-300">Essentiel ✗</span>
                          <span className="text-gray-300">Pro ✗</span>
                          <span className="text-purple-600 font-medium">Multi ✓</span>
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      variant="outline"
                      className="w-full mt-4 text-sm"
                      onClick={() => setShowPricingModal(true)}
                    >
                      Voir le comparatif complet
                    </Button>
                  </div>
                  
                  <Button 
                    onClick={() => {
                      setRegisterForm({...registerForm, selectedPlan});
                      setActiveTab('register');
                    }}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-6"
                  >
                    Commencer avec l&apos;offre {pricingPlans.find(p => p.id === selectedPlan)?.name || 'Pro'}
                  </Button>
                  
                  <p className="text-xs text-center text-gray-500 flex items-center justify-center gap-1">
                    🎁 60 jours d'essai gratuit • Sans engagement
                  </p>
                </div>
              )}

              {/* Login Form */}
              {activeTab === 'login' && (
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Connexion</h2>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label>Email</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="email"
                          value={loginForm.email}
                          onChange={e => setLoginForm({...loginForm, email: e.target.value})}
                          className="pl-10"
                          placeholder="votre@email.fr"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Mot de passe</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="password"
                          value={loginForm.password}
                          onChange={e => setLoginForm({...loginForm, password: e.target.value})}
                          className="pl-10"
                          placeholder="••••••••"
                          required
                        />
                      </div>
                    </div>
                    <Button type="submit" disabled={isLoading} className="w-full bg-green-500 hover:bg-green-600 text-white">
                      {isLoading ? 'Connexion...' : 'Se connecter'}
                    </Button>
                    <p className="text-center text-sm text-gray-500">
                      <a href="/forgot-password" className="text-green-600 hover:underline">Mot de passe oublié ?</a>
                    </p>
                    
                    {/* Divider */}
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">ou</span>
                      </div>
                    </div>
                    
                    {/* Google Login */}
                    <GoogleLoginButton redirectPath="/espace-veterinaire" />
                    
                    <p className="text-center text-sm text-gray-500">
                      Pas encore inscrit ?{' '}
                      <button type="button" onClick={() => setActiveTab('register')} className="text-green-600 hover:underline">
                        Créer un compte
                      </button>
                    </p>
                    <button type="button" onClick={() => setActiveTab('info')} className="w-full text-center text-sm text-gray-400 hover:text-gray-600">
                      ← Retour aux offres
                    </button>
                  </form>
                </div>
              )}

              {/* Register Form */}
              {activeTab === 'register' && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Inscription</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Offre sélectionnée : <strong className="text-green-600">{pricingPlans.find(p => p.id === registerForm.selectedPlan)?.name || 'Pro'}</strong>
                  </p>
                  
                  {/* Promo reminder in form */}
                  <PromoBanner variant="card" />
                  
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label>Nom de la clinique</Label>
                      <div className="relative mt-1">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          value={registerForm.clinicName}
                          onChange={e => setRegisterForm({...registerForm, clinicName: e.target.value})}
                          className="pl-10"
                          placeholder="Clinique Vétérinaire..."
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Prénom</Label>
                        <Input
                          value={registerForm.firstName}
                          onChange={e => setRegisterForm({...registerForm, firstName: e.target.value})}
                          placeholder="Jean"
                          required
                        />
                      </div>
                      <div>
                        <Label>Nom</Label>
                        <Input
                          value={registerForm.lastName}
                          onChange={e => setRegisterForm({...registerForm, lastName: e.target.value})}
                          placeholder="Dupont"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Email professionnel</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="email"
                          value={registerForm.email}
                          onChange={e => setRegisterForm({...registerForm, email: e.target.value})}
                          className="pl-10"
                          placeholder="contact@clinique.fr"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Téléphone</Label>
                      <div className="relative mt-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="tel"
                          value={registerForm.phone}
                          onChange={e => setRegisterForm({...registerForm, phone: e.target.value})}
                          className="pl-10"
                          placeholder="01 23 45 67 89"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Adresse</Label>
                      <div className="relative mt-1">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          value={registerForm.address}
                          onChange={e => setRegisterForm({...registerForm, address: e.target.value})}
                          className="pl-10"
                          placeholder="123 rue de la Clinique"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Ville</Label>
                        <Input
                          value={registerForm.city}
                          onChange={e => setRegisterForm({...registerForm, city: e.target.value})}
                          placeholder="Paris"
                          required
                        />
                      </div>
                      <div>
                        <Label>Code postal</Label>
                        <Input
                          value={registerForm.postalCode}
                          onChange={e => setRegisterForm({...registerForm, postalCode: e.target.value})}
                          placeholder="75001"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Mot de passe</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="password"
                          value={registerForm.password}
                          onChange={e => setRegisterForm({...registerForm, password: e.target.value})}
                          className="pl-10"
                          placeholder="••••••••"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Confirmer le mot de passe</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="password"
                          value={registerForm.confirmPassword}
                          onChange={e => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                          className="pl-10"
                          placeholder="••••••••"
                          required
                        />
                      </div>
                    </div>

                    {/* Plan Selection */}
                    <div>
                      <Label>Choisir votre offre</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {pricingPlans.filter(p => p.active).map(plan => (
                          <button
                            key={plan.id}
                            type="button"
                            onClick={() => setRegisterForm({...registerForm, selectedPlan: plan.id})}
                            className={`p-3 rounded-lg border-2 text-center transition-all ${
                              registerForm.selectedPlan === plan.id 
                                ? 'border-green-500 bg-green-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <p className={`font-bold ${
                              plan.color === 'green' ? 'text-green-600' : 
                              plan.color === 'blue' ? 'text-blue-600' : 'text-purple-600'
                            }`}>{plan.name}</p>
                            <p className="text-lg font-bold">{plan.price}€</p>
                            <p className="text-xs text-gray-500">/mois</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        <strong>60 jours d&apos;essai gratuit</strong>
                      </p>
                      <p className="text-xs text-green-700 mt-1 ml-6">
                        Votre carte bancaire sera enregistrée mais ne sera pas débitée pendant la période d&apos;essai. 
                        Le premier prélèvement aura lieu 60 jours après votre inscription.
                      </p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <strong>Google Agenda requis</strong> - Synchronisez vos RDV automatiquement
                      </p>
                    </div>

                    <Button type="submit" disabled={isLoading} className="w-full bg-green-500 hover:bg-green-600 text-white py-6">
                      {isLoading ? 'Inscription...' : 'Créer mon compte et ajouter ma CB'}
                    </Button>
                    
                    <p className="text-center text-sm text-gray-500">
                      Déjà inscrit ?{' '}
                      <button type="button" onClick={() => setActiveTab('login')} className="text-green-600 hover:underline">
                        Se connecter
                      </button>
                    </p>
                    <button type="button" onClick={() => setActiveTab('info')} className="w-full text-center text-sm text-gray-400 hover:text-gray-600">
                      ← Retour aux offres
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
              Pourquoi choisir vetoRDV ?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Agenda en ligne</h3>
                <p className="text-gray-500">Vos patients prennent rendez-vous 24h/24 directement en ligne.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Rappels automatiques</h3>
                <p className="text-gray-500">Réduisez les absences grâce aux rappels SMS et email.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Statistiques</h3>
                <p className="text-gray-500">Suivez l&apos;activité de votre cabinet en temps réel.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Pricing Comparison Modal */}
      {showPricingModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-gray-50 rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-auto my-4">
            <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-gray-800">Comparatif des forfaits</h2>
              <button
                onClick={() => setShowPricingModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <PricingComparison 
                onSelectPlan={(planId) => {
                  setSelectedPlan(planId);
                  setShowPricingModal(false);
                  setActiveTab('register');
                  setRegisterForm({...registerForm, selectedPlan: planId});
                }}
                selectedPlan={selectedPlan}
              />
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default EspaceVeterinairePage;
